import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import Modal from '../../../components/Modal/Modal'

/**
 * Modal genérico de formulário curto (mensagem, URL de APK, caminho de arquivo...).
 * fields: [{ key, label, placeholder, type: 'text'|'textarea'|'number'|'checkbox', required, defaultValue, hint }]
 * confirmMatch: se definido, exige que o campo `confirm` seja digitado exatamente igual.
 */
export default function FieldsModal({
  isOpen, onClose, title, description, fields = [], submitLabel = 'Enviar',
  danger = false, loading = false, error = null, onSubmit, confirmMatch = null,
}) {
  const [values, setValues] = useState({})

  useEffect(() => {
    if (!isOpen) return
    const init = {}
    for (const f of fields) init[f.key] = f.defaultValue ?? (f.type === 'checkbox' ? false : '')
    setValues(init)
    // Reinicia só ao abrir — `fields` costuma ser literal recriado a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const missing = fields.some((f) => f.required && f.type !== 'checkbox' && !String(values[f.key] ?? '').trim())
  const confirmOk = confirmMatch == null || values.confirm === confirmMatch
  const canSubmit = !missing && confirmOk && !loading

  const handleSubmit = (e) => {
    e.preventDefault()
    if (canSubmit) onSubmit(values)
  }

  const set = (key, v) => setValues((prev) => ({ ...prev, [key]: v }))

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {description && <div className="text-sm text-gray-600">{description}</div>}
        {fields.map((f) => (f.type === 'checkbox' ? (
          <label key={f.key} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={!!values[f.key]} onChange={(e) => set(f.key, e.target.checked)} />
            <span>
              {f.label}
              {f.hint && <span className="block text-xs text-gray-500">{f.hint}</span>}
            </span>
          </label>
        ) : (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea
                rows={3}
                value={values[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            ) : (
              <input
                type={f.type || 'text'}
                value={values[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                autoComplete="off"
              />
            )}
          </div>
        )))}
        {confirmMatch != null && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Digite <span className="font-mono font-semibold">{confirmMatch}</span> para confirmar
            </label>
            <input
              type="text"
              value={values.confirm ?? ''}
              onChange={(e) => set('confirm', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              autoComplete="off"
            />
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={`flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-theme-primary hover:bg-theme-primary/90'}`}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  )
}
